import React, { FC, useState } from 'react';
import { IoBagHandleOutline, IoClose } from 'react-icons/io5';

import { ActionIcon, Box, Menu, Text, TextInput } from '@mantine/core';

import { TextHelpers } from 'helpers/TextHelpers';

import { useComponentSearchFilters } from 'components/component-overview/hooks/use-component-search-filters';
import { SearchService } from 'components/component-overview/services/SearchService';
import { InlineFiltersSection } from 'components/inline-filters/InlineFilters.Section';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useDistributors } from 'hooks/use-distributors';

import cx from './InlineFilters.Distributor.module.scss';

type Props = {
    isViewOnly?: boolean;
};

const InlineFiltersDistributor: FC<Props> = ({ isViewOnly }) => {
    const { distributor: selectedDistributorId } = useComponentSearchFilters();
    const { company: selectedDistributor } = useCompanyProfile(selectedDistributorId || null);

    const [search, setSearch] = useState('');

    const { distributors, isLoading } = useDistributors();

    const matchingDistributors = distributors.filter(({ name }) => TextHelpers.looselyIncludes(name, search));

    return (
        <InlineFiltersSection
            isViewOnly={isViewOnly}
            active={!!selectedDistributor}
            icon={<IoBagHandleOutline size={12} />}
            label={selectedDistributor ? undefined : 'Distributor'}
            body={selectedDistributor?.name}
            onRemove={selectedDistributor ? () => SearchService.setFilter('distributor', undefined) : undefined}
            popoverContent={
                <Box className={cx.options}>
                    <TextInput
                        value={search}
                        className={cx.search}
                        placeholder="Search by name"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)}
                        rightSection={
                            search && (
                                <ActionIcon onClick={() => setSearch('')} size="xs" variant="light">
                                    <IoClose />
                                </ActionIcon>
                            )
                        }
                    />

                    {isLoading && (
                        <Menu.Item disabled>
                            <Text c="dimmed">Loading...</Text>
                        </Menu.Item>
                    )}

                    {matchingDistributors.map((distributor) => (
                        <Menu.Item
                            key={distributor.id}
                            title={distributor.name}
                            classNames={{ itemLabel: cx.optionLabel }}
                            onClick={() => SearchService.setFilter('distributor', distributor.id)}
                        >
                            <Text inherit truncate>
                                {distributor.name}
                            </Text>
                        </Menu.Item>
                    ))}

                    {!matchingDistributors.length && !isLoading && (
                        <Menu.Item disabled>
                            <Text c="dimmed">No distributors found</Text>
                        </Menu.Item>
                    )}
                </Box>
            }
        />
    );
};

export { InlineFiltersDistributor };
